import { useDocumentTitle } from "@/lib/use-document-title";
import { CustomerLayout } from "@/components/CustomerLayout";
import { useEvents, useStores } from "@/lib/data-hooks";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { MapPin, CheckCircle2, Users, Calendar } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";
import { EmptyState, CardSkeletonGrid } from "@/components/EmptyState";
import event1 from "@/assets/event-1.jpg";
import event2 from "@/assets/event-2.jpg";
import event3 from "@/assets/event-3.jpg";
import event4 from "@/assets/event-4.jpg";

const eventImages = [event1, event2, event3, event4];
const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAJ", "JUN", "JUL", "AUG", "SEP", "OKT", "NOV", "DEC"];

function useEventRegistrations(memberId: string | null) {
  return useQuery({
    queryKey: ["my_event_registrations", memberId],
    enabled: !!memberId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_registrations")
        .select("id, event_id")
        .eq("member_id", memberId!);
      if (error) throw error;
      return data ?? [];
    },
  });
}

function useEventAttendeeCounts(eventIds: string[]) {
  return useQuery({
    queryKey: ["event_counts", eventIds.sort().join(",")],
    enabled: eventIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_event_attendee_counts" as any, {
        _event_ids: eventIds,
      });
      if (error) throw error;
      const counts: Record<string, number> = {};
      for (const id of eventIds) counts[id] = 0;
      for (const row of (data ?? []) as Array<{ event_id: string; count: number }>) {
        counts[row.event_id] = Number(row.count) || 0;
      }
      return counts;
    },
  });
}

function EventPage() {
  useDocumentTitle("Event — Lagamobilen Access");
  const { data: events = [], isLoading } = useEvents({ onlyActive: true, upcomingOnly: true });
  const { data: stores = [] } = useStores();
  const { member } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const storeMap = new Map(stores.map((s) => [s.id, s.name]));

  const { data: myRegs = [] } = useEventRegistrations(member?.id ?? null);
  const { data: counts = {} } = useEventAttendeeCounts(events.map((e) => e.id));
  const registeredIds = new Set(myRegs.map((r) => r.event_id));

  const register = useMutation({
    mutationFn: async (eventId: string) => {
      if (!member?.id) throw new Error("Du måste vara inloggad för att boka.");
      const { error } = await supabase.from("event_registrations").insert({
        event_id: eventId,
        member_id: member.id,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my_event_registrations"] });
      qc.invalidateQueries({ queryKey: ["event_counts"] });
    },
  });

  const cancel = useMutation({
    mutationFn: async (eventId: string) => {
      if (!member?.id) return;
      const { error } = await supabase
        .from("event_registrations")
        .delete()
        .eq("event_id", eventId)
        .eq("member_id", member.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my_event_registrations"] });
      qc.invalidateQueries({ queryKey: ["event_counts"] });
    },
  });

  return (
    <CustomerLayout title="Event">
      <p className="text-muted-foreground mb-8 max-w-2xl">Anmäl dig till våra kommande event. Platserna är begränsade.</p>

      {!member && (
        <div className="card-soft p-5 mb-6 max-w-2xl text-sm">
          <Link to="/access/login" className="text-primary underline font-semibold">Logga in</Link> för att kunna boka plats.
        </div>
      )}

      <h2 className="mb-5">Kommande event</h2>
      {isLoading && <CardSkeletonGrid count={4} className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 mb-12" />}
      {!isLoading && !events.length && (
        <EmptyState
          icon={Calendar}
          title="Inga kommande event ännu"
          description="Vi planerar nya event hela tiden. Du får ett mejl när nästa släpps."
          ctaLabel="Tillbaka till översikten"
          ctaTo="/access/dashboard"
        />
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5 mb-12">
        {events.map((e, i) => {
          const d = new Date(e.starts_at);
          const day = d.getDate().toString().padStart(2, "0");
          const month = MONTHS[d.getMonth()];
          const storeName = e.store_id ? storeMap.get(e.store_id) : "";
          const isRegistered = registeredIds.has(e.id);
          const taken = counts[e.id] ?? 0;
          const full = taken >= e.max_attendees && !isRegistered;
          const busy = register.isPending || cancel.isPending;

          return (
            <div key={e.id} className="card-soft overflow-hidden">
              <div
                className="relative h-40 bg-cover bg-center"
                style={{ backgroundImage: `url(${e.cover_image_url || eventImages[i % eventImages.length]})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
                <div className="absolute top-3 left-3 bg-white rounded-md px-3 py-1.5 text-center shadow">
                  <div className="font-display font-bold text-lg leading-none">{day}</div>
                  <div className="text-[10px] tracking-widest text-muted-foreground">{month}</div>
                </div>
              </div>
              <div className="p-5">
                <div className="font-display font-semibold mb-2">{e.title}</div>
                {storeName && <div className="text-xs text-muted-foreground flex items-center gap-1.5"><MapPin size={12} />{storeName}</div>}
                <div className="text-xs text-muted-foreground mt-1">{d.toLocaleString("sv-SE", { dateStyle: "long", timeStyle: "short" })}</div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                  <Users size={12} /> {taken} / {e.max_attendees} platser bokade
                </div>

                {isRegistered ? (
                  <div className="mt-4 space-y-2">
                    <div className="inline-flex items-center gap-1.5 text-sm text-[var(--color-success)] font-display font-semibold">
                      <CheckCircle2 size={16} /> Anmäld
                    </div>
                    <button
                      disabled={busy}
                      onClick={async () => { if (await confirm({ title: "Avboka event?", description: "Din plats släpps till andra medlemmar.", confirmText: "Avboka", destructive: true })) cancel.mutate(e.id); }}
                      className="btn-pill-outline w-full !py-2 !text-xs disabled:opacity-50"
                    >
                      Avboka
                    </button>
                  </div>
                ) : full ? (
                  <button disabled className="btn-pill-outline w-full mt-4 !py-2 !text-xs opacity-50">Fullbokat</button>
                ) : (
                  <button
                    disabled={!member || busy}
                    onClick={() => register.mutate(e.id)}
                    className="btn-pill-outline w-full mt-4 !py-2 !text-xs disabled:opacity-50"
                  >
                    {busy ? "Bokar…" : "Boka plats"}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <h2 className="mb-5">Tidigare event – inspiration</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {eventImages.map((src, i) => (
          <div key={i} className="relative aspect-[4/3] rounded-lg overflow-hidden bg-cover bg-center" style={{ backgroundImage: `url(${src})` }}>
            <div className="absolute inset-0 bg-black/35" />
          </div>
        ))}
      </div>
    </CustomerLayout>
  );
}

export default EventPage;
